/**
 * Rail di navigazione di primo livello (64px, icon-only).
 * Loghi colorati brand per ogni sezione, tooltip al passaggio del mouse.
 * Il click porta DIRETTAMENTE nella sezione (niente pagine intermedie);
 * le sezioni in lavorazione portano alla Home, dove è spiegata la roadmap.
 */
import { BookOpen, Home, ScrollText, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { useUiStore, type AppView } from '../../store/ui';
import { SECTIONS } from '../../lib/sections';
import { useExtUpdateAvailable } from '../../hooks/useExtTokens';

// Viste che appartengono a ciascuna sezione (per lo stato attivo della rail)
const SECTION_VIEWS: Record<string, AppView[]> = {
  mail: ['list', 'feed', 'search', 'folders', 'sent', 'deletion', 'kpi', 'dashboard'],
  linkedin: ['linkedin', 'saved', 'contacts'],
};

// Singola voce della rail: bottone quadrato con tooltip a destra.
function RailItem({
  label,
  active,
  color,
  comingSoon,
  notify,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  color?: string;
  comingSoon?: boolean;
  notify?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <div className="group relative flex justify-center">
      {/* Indicatore di sezione attiva: barra 3px sul bordo sinistro */}
      {active && (
        <span
          className="absolute left-0 top-1/2 h-7 w-[3px] -translate-y-1/2 rounded-r-full"
          style={{ background: color ?? 'var(--portal-accent, #38BDF8)' }}
        />
      )}
      <button
        onClick={onClick}
        aria-label={label}
        className={`relative flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-150 ${
          active ? 'bg-zinc-800/80' : 'hover:bg-zinc-800/60'
        } ${comingSoon ? 'opacity-50 saturate-50 hover:opacity-100 hover:saturate-100' : 'hover:scale-105'}`}
        style={color ? { color } : undefined}
      >
        {children}
        {/* Puntino "in lavorazione" */}
        {comingSoon && (
          <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-500/90" />
        )}
        {/* Puntino "novità/aggiornamento disponibile" (es. nuova estensione) */}
        {notify && !comingSoon && (
          <span className="absolute right-1 top-1 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-zinc-950 bg-violet-400" />
        )}
      </button>
      {/* Tooltip */}
      <div className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-semibold opacity-0 shadow-lg transition-opacity delay-200 duration-100 group-hover:opacity-100"
        style={{ background: '#18181b', color: '#fafafa', border: '1px solid #3f3f46' }}
      >
        {label}
        {comingSoon && (
          <span className="block text-[10px] font-normal text-amber-400/90">In lavorazione</span>
        )}
      </div>
    </div>
  );
}

export function NavRail() {
  const currentView = useUiStore((s) => s.currentView);
  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const openSettings = useUiStore((s) => s.openSettings);
  const setManualiScope = useUiStore((s) => s.setManualiScope);
  // Aggiornamento estensione disponibile: puntino sull'icona LinkedIn.
  const extUpdate = useExtUpdateAvailable();

  return (
    <nav
      aria-label="Sezioni"
      className="flex h-full w-16 shrink-0 flex-col items-stretch gap-1 border-r border-zinc-800/60 bg-zinc-950 py-2"
    >
      {/* Home / hub della piattaforma */}
      <RailItem label="Home" active={currentView === 'home'} onClick={() => setCurrentView('home')}>
        <Home size={24} strokeWidth={1.75} className={currentView === 'home' ? '' : 'text-zinc-400'} />
      </RailItem>

      <div className="mx-3 my-1 border-t border-zinc-800/60" />

      {/* Sezioni (canali) con logo colorato */}
      <div className="flex flex-col gap-1.5">
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = SECTION_VIEWS[s.key]?.includes(currentView) ?? false;
          return (
            <RailItem
              key={s.key}
              label={s.label}
              active={active}
              color={s.color}
              comingSoon={!s.active}
              notify={s.key === 'linkedin' && extUpdate}
              onClick={() => setCurrentView(s.target)}
            >
              <Icon size={26} strokeWidth={2} />
            </RailItem>
          );
        })}
      </div>

      {/* In basso: Novità (changelog) e Configurazione */}
      <div className="mt-auto flex flex-col gap-1 border-t border-zinc-800/60 pt-2">
        <RailItem
          label="Novità e rilasci"
          active={currentView === 'changelog'}
          onClick={() => setCurrentView('changelog')}
        >
          <ScrollText size={22} strokeWidth={1.75} className={currentView === 'changelog' ? '' : 'text-zinc-400'} />
        </RailItem>
        <RailItem
          label="Manuali"
          active={currentView === 'manuali'}
          onClick={() => {
            setManualiScope(null);
            setCurrentView('manuali');
          }}
        >
          <BookOpen size={22} strokeWidth={1.75} className={currentView === 'manuali' ? '' : 'text-zinc-400'} />
        </RailItem>
        <RailItem label="Configurazione" onClick={() => openSettings()}>
          <Settings size={22} strokeWidth={1.75} className="text-zinc-400" />
        </RailItem>
      </div>
    </nav>
  );
}
